// src/components/documents/DocumentTotalsPanel.js — doced-8f3a61c2 (Document editor, Stage 4)
//
// The page's right-hand panel (it stays in view while scrolling; under 1024px it sits below the
// document): status, subtotal, one row per tax (from the totals engine), total, the deposit, Save.
// "+ Add discount" is NOT here — discounts don't exist yet (decision Q3). Review & send is a button
// under this panel (Stage 5f); the Profit card is its own card under it (DocumentProfitCard, Stage 5g).
//
// The status choices and the deposit controls are the estimate pop-up's: Approved can't be chosen
// while a required deposit is unpaid; once a deposit is in play (depositStatus) it can't be changed here.
//
// taxpick4-54573069 — Stage S4: with the Settings › Taxes switch on the page passes `rowLabel` (rows read
// "GST 5%", an exempted part "GST 5% — exempt #…") and `taxChangeNote` (an older estimate a save would
// re-total). Without them (Taxes off) the panel is exactly as before.

import React from 'react';
import { Loader2, AlertCircle, Lock } from 'lucide-react';
import { DEPOSIT_TYPES } from '../../utils/quoteSchema';
import { ESTIMATE_STATUS_CHOICES, taxPartLabel } from '../../utils/estimateEditor';
import { formatCurrency } from '../../utils/formatters';
import { FIELD, SELECT, SELECT_STYLE } from './fieldStyles';

const DEPOSIT_STATUS_WORDS = { paid: 'paid', recorded: 'recorded', refunded: 'refunded', waived: 'waived' };

export default function DocumentTotalsPanel({
    status, onStatus, approvalBlocked, optionalDepositNote,
    totals, taxProblem, onRetryTax, currency, docWordLower,
    deposit, onDeposit, depositLocked, lockedDeposit, depositPreview, stripeReady,
    saving, onSave, saveLabel, error, conflict, onReload, onOverwrite,
    rowLabel, taxChangeNote, // taxpick4-54573069
}) {
    const labelOf = rowLabel || taxPartLabel;
    const choices = ESTIMATE_STATUS_CHOICES.includes(status) || !status ? ESTIMATE_STATUS_CHOICES : [...ESTIMATE_STATUS_CHOICES, status];
    const row = 'flex justify-between gap-3';
    const setDep = (patch) => onDeposit({ ...deposit, ...patch });
    return (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 flex flex-col gap-4">
            <div className="flex justify-between items-center gap-3">
                <label htmlFor="doced-status" className="text-[13px] text-gray-500">Status</label>
                <select id="doced-status" value={status} onChange={(e) => onStatus(e.target.value)}
                    style={SELECT_STYLE} className={`${SELECT} !w-auto min-w-[150px] font-semibold`}>
                    {choices.map((s) => (
                        <option key={s} value={s} disabled={s === 'Approved' && approvalBlocked}>{s}</option>
                    ))}
                </select>
            </div>
            {approvalBlocked && <p className="-mt-2 text-xs text-amber-700">The deposit must be paid before this {docWordLower} can be approved.</p>}
            {optionalDepositNote && <p className="-mt-2 text-xs text-gray-500">{optionalDepositNote}</p>}

            <div className="flex flex-col gap-2 text-sm tabular-nums">
                <div className={row}><span className="text-gray-600">Subtotal</span><span>{formatCurrency(totals.subtotal, currency)}</span></div>
                {totals.taxParts.map((p) => (
                    <div key={p.key} className={row}><span className="text-gray-600">{labelOf(p)}</span><span>{formatCurrency(p.amount, currency)}</span></div>
                ))}
                {totals.taxParts.length === 0 && <div className={row}><span className="text-gray-600">Tax</span><span>{formatCurrency(0, currency)}</span></div>}
                <div className={`${row} border-t border-gray-100 pt-3 text-lg font-extrabold`}><span>Total</span><span>{formatCurrency(totals.total, currency)}</span></div>
            </div>
            {taxChangeNote && (
                <p role="status" className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">{taxChangeNote}</p>
            )}
            {taxProblem && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 flex flex-col gap-2">
                    <span>{taxProblem}</span>
                    {onRetryTax && <button type="button" onClick={onRetryTax} className="self-start min-h-[36px] px-3 rounded-md border border-amber-300 bg-white font-semibold">Try again</button>}
                </div>
            )}

            <div className="border-t border-gray-100 pt-3 flex flex-col gap-2">
                <span className="text-sm font-bold text-gray-900">Deposit</span>
                {depositLocked ? (
                    <div className="flex flex-col gap-1 text-sm text-gray-600">
                        <span>
                            {lockedDeposit?.amountResolved != null ? <>Deposit of <strong className="text-gray-900">{formatCurrency(lockedDeposit.amountResolved, currency)}</strong> — </> : 'Deposit — '}
                            <strong>{DEPOSIT_STATUS_WORDS[lockedDeposit?.depositStatus] || 'unpaid'}</strong>
                        </span>
                        <span className="flex items-center gap-1 text-xs text-gray-500"><Lock size={11} aria-hidden="true" />Set when this {docWordLower} was approved — it can’t be changed here.</span>
                    </div>
                ) : !deposit.enabled ? (
                    <>
                        <span className="text-[13px] text-gray-500">None requested.</span>
                        <button type="button" onClick={() => setDep({ enabled: true })}
                            className="self-start min-h-[44px] px-2 -ml-2 rounded-lg text-sm font-semibold text-[#4f40b0] hover:bg-[#5F4EC9]/10">+ Request a deposit</button>
                    </>
                ) : (
                    <div className="flex flex-col gap-3">
                        <div className="flex gap-2">
                            <div className="flex flex-col gap-1">
                                <label htmlFor="doced-dep-type" className="text-xs font-semibold text-gray-600">Type</label>
                                <select id="doced-dep-type" value={deposit.type} onChange={(e) => setDep({ type: e.target.value })}
                                    style={SELECT_STYLE} className={`${SELECT} min-w-[150px]`}>
                                    <option value={DEPOSIT_TYPES.PERCENTAGE}>Percentage</option>
                                    <option value={DEPOSIT_TYPES.FIXED}>Fixed amount</option>
                                </select>
                            </div>
                            <div className="flex flex-col gap-1 flex-1 min-w-0">
                                <label htmlFor="doced-dep-value" className="text-xs font-semibold text-gray-600">{deposit.type === DEPOSIT_TYPES.PERCENTAGE ? 'Percent of total' : 'Amount'}</label>
                                <input id="doced-dep-value" type="number" inputMode="decimal" min="0" step="any" /* depdraft-3c8e5a14 — arrows move 1 (1% / $1); any typed decimal stays valid */
                                    max={deposit.type === DEPOSIT_TYPES.PERCENTAGE ? '100' : '999999.99'}
                                    placeholder={deposit.type === DEPOSIT_TYPES.PERCENTAGE ? '25' : '500.00'}
                                    value={deposit.value} onChange={(e) => setDep({ value: e.target.value })}
                                    className={`${FIELD} text-right font-semibold`} />
                            </div>
                        </div>
                        <span className="text-xs text-gray-500">
                            {depositPreview > 0 ? <>≈ <strong className="text-gray-900">{formatCurrency(depositPreview, currency)}</strong> on the current total</> : 'Enter an amount to see the deposit.'}
                        </span>
                        {/* depdraft-3c8e5a14 — a Draft can be saved like this; sending can't. */}
                        {!(Number(deposit.value) > 0) && (
                            <p className="text-xs font-medium text-amber-700">Deposit amount needed — you can save this as a draft, but enter the amount before sending.</p>
                        )}
                        <label className="flex items-start gap-2 cursor-pointer text-sm text-gray-700">
                            <input type="checkbox" checked={deposit.required} onChange={(e) => setDep({ required: e.target.checked })} className="w-5 h-5 mt-0.5 accent-[#5F4EC9]" />
                            <span>
                                Require it before approval
                                <span className="block text-xs text-gray-500 mt-0.5">
                                    {deposit.required
                                        ? `The customer signs, then pays. The ${docWordLower} waits in “Awaiting Deposit” and jobs aren’t scheduled until the payment clears.`
                                        : `The ${docWordLower} is approved as soon as the customer signs. The deposit is requested afterwards.`}
                                </span>
                            </span>
                        </label>
                        {!stripeReady && (
                            <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-2">
                                The customer won’t see an online payment button for this {docWordLower}. Arrange the deposit with them directly — by cheque, cash, e-transfer, or your own terminal — and record it on the {docWordLower} once you’ve received it.
                            </p>
                        )}
                        <button type="button" onClick={() => setDep({ enabled: false })}
                            className="self-start min-h-[44px] px-2 -ml-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100">Remove the deposit</button>
                    </div>
                )}
            </div>

            <div className="border-t border-gray-100 pt-4 flex flex-col gap-2">
                {conflict && (
                    <div role="alert" className="p-3 rounded-lg bg-amber-50 border border-amber-300 text-sm text-amber-900 flex flex-col gap-2">
                        <span>{conflict.message}</span>
                        <div className="flex flex-wrap gap-2">
                            <button type="button" onClick={onReload} className="min-h-[44px] px-3 rounded-lg border border-amber-400 bg-white font-semibold">Reload their changes</button>
                            {!conflict.reloadOnly && onOverwrite && (
                                <button type="button" onClick={onOverwrite} className="min-h-[44px] px-3 rounded-lg border border-amber-400 bg-white font-semibold">Save mine over theirs</button>
                            )}
                        </div>
                    </div>
                )}
                {error && (
                    <div role="alert" className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
                        <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden="true" /><span>{error}</span>
                    </div>
                )}
                <button type="button" onClick={onSave} disabled={saving}
                    className="min-h-[46px] rounded-lg bg-[#5F4EC9] text-[15px] font-bold text-white hover:bg-[#4f40b0] disabled:opacity-60 inline-flex items-center justify-center gap-2">
                    {saving && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}{saving ? 'Saving…' : saveLabel}
                </button>
            </div>
        </div>
    );
}
